import { Widget } from "@foretagsplatsen/widgetjs";
import WizardFooterWidget from "./WizardFooterWidget/WizardFooterWidget.js";
import WizardHeaderWidget from "./WizardHeaderWidget/WizardHeaderWidget.js";
import WizardStepWidget from "./WizardStepWidget.js";

/**
 * Widget to render a wizard passed as argument.
 *
 * @param {wizard} wizard - A wizard with steps
 * @param {Renderable} header - Renderable used to render the header
 *   of the wizard widget
 * @param {function} [cancelCallback] - A function called when the
 *   user uses the 'Cancel' button
 * @param {Renderer} [titleRenderer] - The function is passed the current
 *   and last step 1-based indices as parameters and is expected to
 *   return a title for the header
 * @param {Renderable} [footer] - Footer to render at the bottom of the WizardWidget.
 *   It should provide a way to navigate between steps
 * @param {Renderable} [loader] - When provided, this will be rendered before the wizard
 *   is started, in place of the step content
 * @param {SupportInfo} [supportInfo] - Contact information to display
 *   in case of an error
 */
export default class WizardWidget extends Widget {
	constructor({
		wizard,
		loader = null,
		header = new WizardHeaderWidget({ wizard }),
		footer = new WizardFooterWidget({ wizard }),
		supportInfo,
	}) {
		super(...arguments);

		this._wizard = wizard;
		this._header = header;
		this._footer = footer;
		this._loader = loader;

		this._wizard.currentStepChanged.register(() => this.update());
		this._wizard.currentStepSubmitted.register(() => this.update());

		this._stepWidget = new WizardStepWidget({
			wizard: this._wizard,
			supportInfo,
		});
	}

	start(...args) {
		return this._wizard.start(...args);
	}

	renderContentOn(html) {
		if (!this._wizard.isStarted()) {
			if (this._loader) {
				html.div(
					{ klass: "wizard-container new-wizard" },
					this._renderHeaderOn.bind(this),
					html.div(
						{ klass: "wizard-content clearfix" },
						this._loader,
					),
				);
			}
			return;
		}

		html.div(
			{ klass: "wizard-container new-wizard", "data-test": "wizard" },
			this._renderHeaderOn.bind(this),
			this._renderBodyOn.bind(this),
			this._renderFooterOn.bind(this),
		);
	}

	_renderHeaderOn(html) {
		html.div({ klass: "wizard-header" }, this._header);
	}

	_renderBodyOn(html) {
		html.div({ klass: "wizard-content clearfix" }, this._stepWidget);
	}

	_renderFooterOn(html) {
		html.div({ klass: "wizard-footer" }, this._footer);
	}
}
